import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ClinicalTagChipBox } from '@/components/report/ClinicalTagChipBox';
import { DiagnosisTagChips } from '@/components/shared/DiagnosisTagChips';
import { useHasClinicPermission } from '@/hooks/use-clinic-permission';
import { useReplaceStudyTags, useStudyTags } from '@/hooks/use-study-tags';
import { clinicTagScope } from '@/lib/api/report-tags';
import { mergeClinicalTags } from '@/lib/clinicalTags';
import { useTranslation } from 'react-i18next';
const QUALIFIER_CYCLE = {
    present: 'suspected',
    suspected: 'ruled_out',
    ruled_out: 'present',
};
// The optimistic cache names a not-yet-created tag `new:<label>` (use-study-tags).
const isPendingTagId = (tagId) => tagId.startsWith('new:');
function toDraft(link) {
    return isPendingTagId(link.tagId)
        ? { tagId: null, newTag: { label: link.label }, label: link.label, qualifier: link.qualifier }
        : { tagId: link.tagId, label: link.label, qualifier: link.qualifier };
}
/**
 * The expanded worklist card's "Clinical tags" block. It shows the study's
 * finalized reports' tags (fixed here: they belong to the signed report) and
 * the tags put on the study itself, which people who write reports at the
 * study's clinic (create_report) can add, remove and re-qualify. Each change
 * saves at once. A study read through a share shows only its report tags.
 */
export function StudyClinicalTagsField({ study }) {
    const { t } = useTranslation();
    const readOnly = study.readOnly === true;
    const canEdit = useHasClinicPermission(study.clinicId, 'create_report') && !readOnly;
    // Only an editor needs the full set (ruled-out tags included); everyone else
    // reads the row's copy. A shared-in study is never fetched: the server refuses it.
    const linksQuery = useStudyTags(study.id, canEdit);
    const replace = useReplaceStudyTags(study.id);
    const studyLinks = linksQuery.data;
    const editable = canEdit && studyLinks !== undefined;
    const reportTags = study.tags ?? [];
    const reportLabels = new Set(reportTags.map((tag) => tag.label.toLowerCase()));
    const chips = [
        ...reportTags.map((tag) => ({
            key: `report:${tag.label.toLowerCase()}`,
            label: tag.label,
            qualifier: tag.qualifier,
            locked: true,
            lockedTitle: t('worklist.studyTags.fromReport', { label: tag.label }),
            // The study's own link of this label hides behind the report's chip: its X
            // still removes that link (the report's tag stays).
            removeKey: studyLinks?.find((link) => link.label.toLowerCase() === tag.label.toLowerCase())
                ?.tagId,
        })),
        // A label on both a report and the study shows once, as the report's.
        ...(studyLinks ?? [])
            .filter((link) => !reportLabels.has(link.label.toLowerCase()))
            .map((link) => ({ key: link.tagId, label: link.label, qualifier: link.qualifier })),
    ];
    // Read-only (no create_report, or a shared-in study): the row's own copy, as the
    // card always showed it.
    const readOnlyTags = mergeClinicalTags(study.tags, study.studyTags);
    const save = (drafts) => replace.mutate(drafts);
    const current = () => (studyLinks ?? []).map(toDraft);
    // The chip box only offers a label that is not on a chip yet (it owns that rule),
    // and every study link's label is on one, its own or the report's.
    const addDraft = (draft) => save([...current(), draft]);
    const onAddOption = (option) => addDraft(option.id
        ? { tagId: option.id, label: option.label, qualifier: 'present' }
        : {
            tagId: null,
            newTag: {
                label: option.label,
                ...(option.aliases.length > 0 && { aliases: option.aliases }),
                ...(option.category && { category: option.category }),
            },
            label: option.label,
            qualifier: 'present',
        });
    const onAddFreeText = (label) => addDraft({ tagId: null, newTag: { label }, label, qualifier: 'present' });
    const onRemove = (key) => save((studyLinks ?? []).filter((link) => link.tagId !== key).map(toDraft));
    const onCycle = (key) => save((studyLinks ?? []).map((link) => toDraft(link.tagId === key ? { ...link, qualifier: QUALIFIER_CYCLE[link.qualifier] } : link)));
    return (_jsxs("section", { className: "border-b border-[#333] px-2 py-2.5", "aria-labelledby": `study-clinical-tags-${study.id}`, "data-testid": "study-clinical-tags", children: [_jsx("span", { id: `study-clinical-tags-${study.id}`, className: "text-muted-foreground text-xs font-medium tracking-wide uppercase", children: t('worklist.clinicalTags') }), editable ? (
            // Same test id as the read-only chips: either way this is the card's chip list.
            _jsx("div", { className: "mt-1.5", "data-testid": "diagnosis-tag-chips", children: _jsx(ClinicalTagChipBox, { chips: chips, scope: clinicTagScope(study.clinicId), readOnly: false, onCycle: onCycle, onRemove: onRemove, onAddOption: onAddOption, onAddFreeText: onAddFreeText, testIdPrefix: "study-tags" }) })) : readOnlyTags.length > 0 ? (_jsx(DiagnosisTagChips, { tags: readOnlyTags, max: 12, fullLabels: true, className: "mt-1.5" })) : (_jsx("p", { className: "text-muted-foreground/60 mt-1.5 text-xs", children: t('worklist.studyTags.none') }))] }));
}
